import {useCallback, useEffect, useRef} from 'react';
import {useLoadScript} from '../useLoadScript';
import {useEvent} from '@chekinapp/ui';
import {isHPPDataError} from '@chekin/core';

type Props = {
  hppData: unknown;
  onError: (error?: Error | string) => void;
  onLoad?: () => void;
  onSuccess?: () => void;
  onInit?: () => void;
  enabled?: boolean;
};

export const useAddonPayments = ({
  onError,
  onInit,
  onLoad,
  onSuccess,
  hppData,
  enabled = true,
}: Props) => {
  const isInitializedRef = useRef(false);

  const handleError = useEvent(onError);
  const handleSuccess = useEvent(onSuccess);
  const handleInit = useEvent(onInit);
  const handleLoad = useEvent(onLoad);

  const {
    isLoading: isScriptLoading,
    isLoaded: isScriptLoaded,
    error: scriptError,
  } = useLoadScript({
    src: '/assets/js/rxp-js.min.js',
    id: 'rxp-js-script',
    enabled,
  });

  useEffect(() => {
    if (scriptError) {
      handleError(scriptError);
    }
  }, [scriptError, handleError]);

  const initializeHpp = useCallback(() => {
    if (!isScriptLoaded || isScriptLoading) {
      return;
    }

    if (!window.RealexHpp) {
      handleError(new Error('RealexHpp library not loaded'));
      return;
    }

    if (isInitializedRef.current) {
      handleLoad();
      return;
    }

    const hppUrl =
      process.env.NODE_ENV === 'production'
        ? 'https://pay.realexpayments.com/pay'
        : 'https://pay.sandbox.realexpayments.com/pay';

    window.RealexHpp.setHppUrl(hppUrl);

    if (import.meta.env.MODE === 'development') {
      window.RealexHpp.setConfigItem('enableLogging', true);
      window.addEventListener(window.RealexHpp.constants.logEventName, function (e) {
        console.log((e as {detail?: string}).detail);
      });
    }

    window.RealexHpp.embedded.init(
      'autoload',
      'global-payment-iframe',
      async hppResponse => {
        handleInit?.();
        if (isHPPDataError(hppResponse)) {
          handleError(hppResponse.message);
        } else if (hppResponse.RESULT !== '00') {
          handleError(hppResponse.MESSAGE);
        } else {
          handleSuccess?.();
        }
      },
      hppData,
      {
        onResize: (dimensions: {width: string; height: string}) => {
          const iframe = document.getElementById(
            'global-payment-iframe',
          ) as HTMLIFrameElement;
          if (iframe) {
            iframe.style.width = dimensions.width;
            const heightValue = parseInt(dimensions.height, 10);
            iframe.style.height = `${heightValue + 1}px`;
          }
        },
      },
    );

    handleLoad();
    isInitializedRef.current = true;
  }, [
    isScriptLoaded,
    isScriptLoading,
    handleError,
    handleSuccess,
    handleInit,
    handleLoad,
    hppData,
  ]);

  const reset = useCallback(() => {
    isInitializedRef.current = false;
  }, []);

  const hasHppData = !!hppData;
  useEffect(() => {
    if (enabled && isScriptLoaded && !isScriptLoading && hasHppData) {
      initializeHpp();
    }
  }, [enabled, initializeHpp, isScriptLoaded, isScriptLoading, hasHppData]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);
};
